import React, { useEffect, useState } from "react";
import { moduleApi } from "../../lib/api";
import { DEFAULT_TAKEN_BY_OPTIONS, DEFAULT_CASHED_BY_OPTIONS, mapBoardFromApi, type Board } from "./types";
import { Dashboard } from "./Dashboard";
import { BoardDetail } from "./BoardDetail";
import { ArchivedBoards } from "./ArchivedBoards";
import { GlobalSummary } from "./GlobalSummary";

type View = "dashboard" | "detail" | "archives" | "global";

export const ExceptionServiceModule: React.FC = () => {
  const [view, setView] = useState<View>("dashboard");
  const [boards, setBoards] = useState<Board[]>([]);
  const [selectedBoardId, setSelectedBoardId] = useState<number | null>(null);

  const selectedBoard = boards.find((b) => b.id === selectedBoardId) ?? boards[0];
  const effectiveTakenByOptions = selectedBoard?.takenByOptions ?? DEFAULT_TAKEN_BY_OPTIONS;
  const effectiveCashedByOptions = selectedBoard?.cashedByOptions ?? DEFAULT_CASHED_BY_OPTIONS;

  useEffect(() => {
    (async () => {
      try {
        const data = await moduleApi.getExceptionServiceBoards();
        if (Array.isArray(data)) {
          const mapped = data.map(mapBoardFromApi);
          setBoards(mapped);
          if (mapped.length > 0) setSelectedBoardId(mapped[0].id);
        }
      } catch (e) {
        console.error("Failed to load boards", e);
      }
    })();
  }, []);

  const handleCreateBoard = async (title: string) => {
    try {
      const res: any = await moduleApi.createExceptionServiceBoard({
        title,
        taken_by_options: DEFAULT_TAKEN_BY_OPTIONS,
        cashed_by_options: DEFAULT_CASHED_BY_OPTIONS,
      });
      const newBoard: Board = {
        id: res.id,
        title,
        createdAt: new Date().toLocaleDateString("fr-FR"),
        isArchived: false,
        takenByOptions: DEFAULT_TAKEN_BY_OPTIONS,
        cashedByOptions: DEFAULT_CASHED_BY_OPTIONS,
      };
      setBoards((prev) => [...prev, newBoard]);
    } catch (e) {
      console.error("Failed to create board", e);
    }
  };

  const handleOpenBoard = (id: number) => {
    setSelectedBoardId(id);
    setView("detail");
  };

  const handleArchiveBoard = async (id: number) => {
    if (!confirm("Archiver ce tableau ? Il ne sera plus modifiable et ne pourra plus être désarchivé.")) return;
    try {
      await moduleApi.updateExceptionServiceBoard(id, { is_archived: true });
    } catch (e) {
      console.error("Failed to archive board", e);
    }
    setBoards((prev) => prev.map((b) => (b.id === id ? { ...b, isArchived: true } : b)));
  };

  const handleDeleteBoard = async (id: number) => {
    if (!confirm("Supprimer définitivement ce tableau et toutes ses réservations ?")) return;
    try {
      await moduleApi.deleteExceptionServiceBoard(id);
    } catch (e) {
      console.error("Failed to delete board", e);
    }
    setBoards((prev) => {
      const next = prev.filter((b) => b.id !== id);
      if (selectedBoardId === id) {
        const first = next[0];
        setSelectedBoardId(first ? first.id : null);
        setView("dashboard");
      }
      return next;
    });
  };

  const handleUpdateSettings = async ({ title, takenByOptions, cashedByOptions }: { title: string; takenByOptions: string[]; cashedByOptions: string[] }) => {
    if (!selectedBoard) return;
    try {
      await moduleApi.updateExceptionServiceBoard(selectedBoard.id, {
        title,
        taken_by_options: takenByOptions,
        cashed_by_options: cashedByOptions,
      });
    } catch (e) {
      console.error("Failed to update board settings", e);
    }
    setBoards((prev) => prev.map((b) => (b.id === selectedBoard.id ? { ...b, title, takenByOptions, cashedByOptions } : b)));
  };

  if (view === "detail") {
    return (
      <BoardDetail
        onBack={() => setView("dashboard")}
        board={selectedBoard}
        takenByOptions={effectiveTakenByOptions}
        cashedByOptions={effectiveCashedByOptions}
        onUpdateSettings={handleUpdateSettings}
      />
    );
  }
  if (view === "archives") {
    return <ArchivedBoards boards={boards} onBack={() => setView("dashboard")} onOpenBoard={handleOpenBoard} />;
  }
  if (view === "global") {
    return <GlobalSummary boards={boards} onBack={() => setView("dashboard")} />;
  }
  return (
    <Dashboard
      boards={boards}
      onOpenBoard={handleOpenBoard}
      onOpenArchives={() => setView("archives")}
      onArchive={handleArchiveBoard}
      onCreateBoard={handleCreateBoard}
      onOpenGlobal={() => setView("global")}
      onDeleteBoard={handleDeleteBoard}
    />
  );
};
